import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectedSocietyStorer } from '../../../utils/webStorer';

// Query state and loading for any VMS list screen. All seven endpoints share
// the same contract — page/limit plus a handful of scalar filters, answering
// with data + meta.pagination — so one hook drives every one of them.
//
//   const list = useVmsList({ slice: 'visits', action: getVisitsList,
//                             filters: { status: '', flatId: '' } });
//
// `filters` doubles as the initial state and the reset target, so a screen
// declares its filter set once.
export const useVmsList = ({ slice, action, filters: initialFilters = {}, limit = 10 }) => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const sliceState = useSelector(state => state.VmsReducer?.[slice]) || {};

    const [filters, setFilters] = useState(initialFilters);
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(limit);

    // The initial filter object is a fresh literal on every render, so it is
    // pinned once — otherwise reset() would change identity each pass and
    // retrigger every effect that depends on it.
    const defaultsRef = useRef(initialFilters);

    // Filters are compared by value, not identity: the object is rebuilt each
    // render but the request should only refire when a value actually changes.
    const filterKey = JSON.stringify(filters);

    const fetchList = useCallback(() => {
        if (!societyId) return;
        // society_id is sent explicitly: the X-Society-Id header authorises the
        // call but does not scope the rows, so omitting it returns every
        // society's records.
        dispatch(action({ societyId, page, limit: itemsPerPage, ...JSON.parse(filterKey) }));
    }, [dispatch, action, societyId, page, itemsPerPage, filterKey]);

    useEffect(() => { fetchList(); }, [fetchList]);

    // Changing a filter always returns to page 1 — staying on page 4 of a
    // narrower result set would show an empty screen.
    const setFilter = useCallback((key, value) => {
        setFilters(f => ({ ...f, [key]: value }));
        setPage(1);
    }, []);

    const resetFilters = useCallback(() => {
        setFilters(defaultsRef.current);
        setPage(1);
    }, []);

    const hasFilters = useMemo(
        () => Object.entries(filters).some(([k, v]) => v !== defaultsRef.current[k] && v !== '' && v != null),
        [filters]
    );

    const rows = useMemo(
        () => (Array.isArray(sliceState.list) ? sliceState.list : []),
        [sliceState.list]
    );

    return {
        societyId,
        rows,
        loading: !!sliceState.listLoading,
        totalPages: sliceState.pagination?.totalPages || 1,
        // Always from meta.pagination — rows.length is only the current page.
        totalItems: sliceState.pagination?.total ?? rows.length,

        filters,
        setFilter,
        resetFilters,
        hasFilters,

        page,
        setPage,
        itemsPerPage,
        setItemsPerPage: useCallback((n) => { setItemsPerPage(n); setPage(1); }, []),

        refresh: fetchList,
    };
};

export default useVmsList;
